import { useEffect, useRef, useState } from 'react';
import { ASSET_UNIT_ROW_REASON_MESSAGES } from '@siliconx/shared/dto/asset-unit';
import type { AssetUnitRowReason } from '@siliconx/shared/dto/asset-unit';
import { pickSerial } from '../../lib/unitSerialScan';

/**
 * SIL-1917: mobile-width camera capture of unit serials for one stock line.
 * Scan -> show decoded value -> confirm -> next. Decoding is in-browser
 * (native BarcodeDetector when it supports our formats, else the shared
 * zxing ponyfill chunk); no image is stored or uploaded. Without a camera or
 * permission, or when a label will not decode, the typed field on the same
 * screen is the path. Saving is delegated to the caller (same add API as
 * manual entry, source 'camera').
 */

export type CaptureSave = (serial: string, source: 'camera' | 'manual') => Promise<{ ok: true } | { ok: false; reason: AssetUnitRowReason | 'error' }>;

type Phase = 'starting' | 'scanning' | 'no_camera';
const SCAN_INTERVAL_MS = 250;

export function UnitCameraCapture({
  captured,
  quantity,
  onSave,
  onClose,
}: {
  captured: number;
  quantity: number;
  onSave: CaptureSave;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [phase, setPhase] = useState<Phase>('starting');
  const [candidate, setCandidate] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const candidateRef = useRef<string | null>(null);
  candidateRef.current = candidate;

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let stopped = false;

    async function start() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('no_camera');
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play().catch(() => undefined);
        const { createDetector } = await import('../scan/barcode');
        const detector = await createDetector();
        if (stopped) return;
        setPhase('scanning');
        let inFlight = false;
        timer = setInterval(() => {
          // Hold still while a candidate awaits confirmation.
          if (inFlight || candidateRef.current !== null || video.readyState < 2) return;
          inFlight = true;
          detector
            .detect(video)
            .then((found) => {
              const verdict = pickSerial(found.map((f) => f.rawValue));
              if (!verdict) return;
              if (verdict.kind === 'serial') {
                setNote(null);
                setCandidate(verdict.value);
              } else if (verdict.kind === 'express_code') {
                setNote('That is the express service code, not the service tag. Scan the service tag.');
              } else {
                setNote('Could not read a serial from that code. Try again or type it below.');
              }
            })
            .catch(() => undefined)
            .finally(() => {
              inFlight = false;
            });
        }, SCAN_INTERVAL_MS);
      } catch {
        if (!stopped) setPhase('no_camera');
      }
    }
    void start();
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  async function save(serial: string, fromTyped: boolean) {
    setBusy(true);
    const r = await onSave(serial, fromTyped ? 'manual' : 'camera');
    setBusy(false);
    if (r.ok) {
      setNote(null);
      setCandidate(null);
      if (fromTyped) setTyped('');
    } else {
      // Duplicate or other rejection: clear warning, nothing saved, keep scanning.
      setNote(r.reason === 'error' ? 'Could not save. Try again.' : ASSET_UNIT_ROW_REASON_MESSAGES[r.reason]);
      setCandidate(null);
    }
  }

  const full = captured >= quantity;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background" role="dialog" aria-label="Scan unit serials" data-testid="unit-camera-capture">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <p className="text-sm font-semibold text-foreground" data-testid="capture-progress">
          {captured.toLocaleString()} of {quantity.toLocaleString()}
        </p>
        <button type="button" className="text-sm font-medium text-blue-600" onClick={onClose}>
          Done
        </button>
      </div>

      <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
        {phase !== 'no_camera' && (
          <video ref={videoRef} playsInline muted className="aspect-[4/3] w-full rounded-md bg-black object-cover" data-testid="capture-video" />
        )}
        {phase === 'no_camera' && (
          <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground" data-testid="capture-no-camera">
            Camera unavailable. Type the serial below.
          </p>
        )}
        {phase === 'starting' && <p className="text-sm text-muted-foreground">Starting camera...</p>}

        {full && <p className="text-sm font-medium text-foreground">All units on this line have a serial.</p>}

        {candidate !== null && !full && (
          <div className="rounded-md border border-border px-3 py-2" data-testid="capture-candidate">
            <p className="font-mono text-lg">{candidate}</p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                disabled={busy}
                className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                onClick={() => void save(candidate, false)}
              >
                Confirm
              </button>
              <button type="button" disabled={busy} className="flex-1 rounded-md border border-border px-3 py-2 text-sm" onClick={() => setCandidate(null)}>
                Rescan
              </button>
            </div>
          </div>
        )}

        {note && (
          <p role="alert" className="text-sm text-red-600" data-testid="capture-note">
            {note}
          </p>
        )}

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (typed.trim() !== '' && !full) void save(typed.trim(), true);
          }}
        >
          <input
            aria-label="Type serial"
            className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-2 font-mono text-sm"
            placeholder="Type serial / service tag"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={full || busy}
            autoComplete="off"
          />
          <button type="submit" disabled={full || busy || typed.trim() === ''} className="rounded-md border border-border px-3 py-2 text-sm font-medium disabled:opacity-50">
            Add
          </button>
        </form>
      </div>
    </div>
  );
}
